import * as methods from 'api/core/actions';
import { z } from 'zod';
import { type AgentSession, parseAgentSessionsResponse } from './agentSessions.api';

import { projectSuggestionSchema as suggestion } from './projectSuggestions.schema';

export type { ProjectSuggestion } from './projectSuggestions.schema';

const response = z.object({
  suggestion: suggestion.nullable(),
  prediction: z
    .object({ request_id: z.string().nullable(), state: z.string().nullable() })
    .optional(),
});

export type UnfinishedReason =
  | 'failed'
  | 'stale'
  | 'no_projects'
  | 'limit_reached'
  | 'delayed'
  | 'replaced'
  | 'pending'
  | 'offline';

/** A prediction that ended without a result; `reason` tells callers whether retrying can help. */
export class PredictionUnfinished extends Error {
  constructor(
    readonly reason: UnfinishedReason,
    message: string
  ) {
    super(message);
  }
}

/** Why a prediction ended without a result, worded so the person knows what to do next. */
const unfinishedMessages: Partial<Record<UnfinishedReason, string>> = {
  failed: 'The suggestion service didn’t respond. Try again in a moment.',
  stale: 'This period changed while we were checking it. Try again to use the latest activity.',
  no_projects: 'There are no projects you can be assigned to yet, so there is nothing to suggest.',
  limit_reached:
    'You’ve reached today’s limit for automatic suggestions. You can still pick a project.',
  delayed: 'This check is taking much longer than usual. Try again.',
};

/** The browser could not reach the server at all, as opposed to the server answering with an error. */
// A fetch that never reached the server rejects with a TypeError; a server answer
// received while the browser happens to flag itself offline is not a network failure.
export const isNetworkFailure = (error: unknown) =>
  error instanceof TypeError &&
  ((typeof navigator !== 'undefined' && navigator.onLine === false) ||
    /failed to fetch|networkerror|load failed|network request failed/i.test(error.message));

export const OFFLINE_MESSAGE =
  'You’re offline. Check your connection and try again. Any check already started keeps running.';

/** Reported while waiting, so the picker can say why nothing has arrived yet. */
export type PredictionWait = 'checking' | 'offline';

// Keep polling through a dropped connection for a few minutes: the job runs on
// the server regardless, and the result is still wanted once the network returns.
const OFFLINE_RETRY_MS = 2000;
const OFFLINE_RETRIES = 90;

/** Poll reads only. A failed poll never sends another generation request. */
export async function waitForProjectSuggestion(
  initial: unknown,
  read: () => Promise<unknown>,
  pause = (milliseconds: number) => new Promise<void>(resolve => setTimeout(resolve, milliseconds)),
  onWait?: (wait: PredictionWait) => void
) {
  const readThroughOutages = async () => {
    for (let retry = 0; ; retry++) {
      try {
        const value = await read();
        if (retry) onWait?.('checking');
        return value;
      } catch (error) {
        if (!isNetworkFailure(error)) throw error;
        if (retry >= OFFLINE_RETRIES) throw new PredictionUnfinished('offline', OFFLINE_MESSAGE);
        if (!retry) onWait?.('offline');
        await pause(OFFLINE_RETRY_MS);
      }
    }
  };
  let current = response.parse(initial);
  const requestId = current.prediction?.request_id;
  for (let attempt = 0; attempt <= 90; attempt++) {
    const state = current.prediction?.state;
    if (requestId && current.prediction?.request_id !== requestId)
      throw new PredictionUnfinished(
        'replaced',
        'This prediction was replaced by a newer request. Reopen the project picker.'
      );
    if (state !== 'queued' && state !== 'running') {
      const message = unfinishedMessages[state as UnfinishedReason];
      if (message) throw new PredictionUnfinished(state as UnfinishedReason, message);
      if (['review', 'confirmed', 'applied'].includes(state ?? '') && !current.suggestion)
        throw new Error('This suggestion is no longer available. Please generate a new one.');
      return current.suggestion;
    }
    if (attempt === 90) break;
    // Poll frequently during the first few seconds so fast interactive jobs do
    // not wait for a long backoff. Slow jobs still get about two minutes.
    const delay =
      attempt < 12
        ? Math.min(100 * 2 ** attempt, 250)
        : Math.min(500 * 2 ** Math.min(attempt - 12, 2), 1500);
    await pause(delay);
    current = response.parse(await readThroughOutages());
  }
  throw new PredictionUnfinished(
    'pending',
    'The project check is still pending. It keeps running; check again in a moment.'
  );
}
const url = (sessionId: number, periodId: number) =>
  `agent_sessions/${sessionId}/periods/${periodId}/project_suggestion`;

export async function getProjectSuggestion(sessionId: number, periodId: number) {
  return response.parse(await methods.Get(url(sessionId, periodId))).suggestion;
}
export async function generateProjectSuggestion(
  sessionId: number,
  periodId: number,
  onWait?: (wait: PredictionWait) => void
) {
  const endpoint = url(sessionId, periodId);
  let initial: unknown;
  try {
    initial = await methods.Post(endpoint, { force: true, async: true });
  } catch (error) {
    // Safe to repeat: the server ignores a second request while one is in flight.
    if (isNetworkFailure(error)) throw new PredictionUnfinished('offline', OFFLINE_MESSAGE);
    throw error;
  }
  return waitForProjectSuggestion(initial, () => methods.Get(endpoint), undefined, onWait);
}
export async function acceptProjectSuggestion(
  sessionId: number,
  periodId: number,
  fingerprint: string
): Promise<AgentSession> {
  const raw = z
    .object({ session: z.unknown() })
    .parse(
      await methods.Patch(`${url(sessionId, periodId)}?compact_suggestions=true`, { fingerprint })
    );
  return readSession(raw.session);
}

export async function getSessionProjectSuggestions(sessionId: number) {
  return z
    .object({ suggestions: z.array(z.object({ segment_id: z.number(), suggestion })) })
    .parse(
      await methods.Get(`agent_sessions/${sessionId}/project_suggestions?compact_suggestions=true`)
    ).suggestions;
}

const readSession = (value: unknown) =>
  parseAgentSessionsResponse({
    sessions: [value],
    period: { from: '', to: '' },
    truncated: false,
    limit: 1,
  }).sessions[0];

/**
 * `fillSegmentIds` are unassigned periods without their own suggestion. Rails
 * gives them the accepted project as the session's carried-forward project and
 * covers them with the same undo token.
 */
export async function acceptSessionProjectSuggestions(
  sessionId: number,
  suggestions: { segment_id: number; fingerprint: string; project_id?: number }[],
  fillSegmentIds: number[] = []
) {
  const result = z
    .object({ session: z.unknown(), undo_token: z.string() })
    .parse(
      await methods.Patch(
        `agent_sessions/${sessionId}/project_suggestions?compact_suggestions=true`,
        fillSegmentIds.length ? { suggestions, fill_segment_ids: fillSegmentIds } : { suggestions }
      )
    );
  return { session: readSession(result.session), undoToken: result.undo_token };
}

export async function undoSessionProjectSuggestions(sessionId: number, undoToken: string) {
  const result = z.object({ session: z.unknown() }).parse(
    await methods.Post(
      `agent_sessions/${sessionId}/project_suggestions/undo?compact_suggestions=true`,
      {
        undo_token: undoToken,
      }
    )
  );
  return readSession(result.session);
}
